import { hexToRGBA, randomString } from '@/composables/helperFunctions'
import Highcharts, { AlignValue, AnnotationMockPointOptionsObject, Chart, VerticalAlignValue } from 'highcharts'
import { useIncidentStore } from '@/store/incident'
import { BarnDocIncidentMessagePayload, Incident } from '@/types/Incident'
import { Component } from 'vue'
import IncidentDisplay from '@/components/incidents/IncidentDisplay.vue'

import i18n from '@/plugins/i18n.js'
import { DocumentQuerySelect } from '@/types/Query'
const { t } = i18n.global
export const ANNOTATOR_TYPES = {
  TIMESPAN: 'timespan',
  BARNDOC: 'barnDoc',
  SPARSE_DICT_ANOMALY: 'sparseDictAnomaly',
} as const

export type AnnotatorType = (typeof ANNOTATOR_TYPES)[keyof typeof ANNOTATOR_TYPES]

interface SerializedChartIncidentAnnotator {
  annotatorType: AnnotatorType
  additionalFilters?: DocumentQuerySelect[]
  title?: string
}

interface BaseSerializedTimespanChartIncidentAnnotator extends SerializedChartIncidentAnnotator {
  shapeFillColor?: string
}

export interface SerializedTimespanChartIncidentAnnotator extends BaseSerializedTimespanChartIncidentAnnotator {
  incidentTypes: string[]
}

export interface SerializedBarnDocChartAnnotationProvider extends BaseSerializedTimespanChartIncidentAnnotator {
  annotatorType: typeof ANNOTATOR_TYPES.BARNDOC
}

export interface SerializedSparseDictChartAnnotationProvider extends BaseSerializedTimespanChartIncidentAnnotator {
  annotatorType: typeof ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY
  detectionIdentifier?: string
}

export type SerializedChartAnnotatorType =
  | SerializedTimespanChartIncidentAnnotator
  | SerializedBarnDocChartAnnotationProvider
  | SerializedSparseDictChartAnnotationProvider

export abstract class ChartIncidentAnnotator {
  get annotatorId(): string {
    return this._annotatorId
  }
  abstract readonly annotatorType: AnnotatorType
  private readonly chartEntity: string | undefined
  private annotations: Highcharts.AnnotationsOptions[] = []
  private readonly _annotatorId: string
  protected additionalFilters: DocumentQuerySelect[] = []
  protected transientAdditionalFilters: DocumentQuerySelect[] = []
  protected incidents: Incident[] = []
  protected title?: string

  protected constructor(chartEntity: string | undefined) {
    this.chartEntity = chartEntity
    this._annotatorId = randomString(20)
  }

  protected compileWrappedOrFilters(): DocumentQuerySelect[][] | undefined {
    const filters: DocumentQuerySelect[][] = [
      [
        { key: 'hidden', operator: '==', value: false },
        { key: 'hidden', operator: 'exists', value: false },
      ],
    ]
    if (this.chartEntity) {
      filters.push([
        { key: 'displayConfiguration.includeIn', operator: 'contains', value: this.chartEntity },
        { key: 'displayConfiguration.includeIn', operator: 'contains', value: 'frontend-charts.all' },
        { key: 'displayConfiguration.includeIn', operator: 'exists', value: false },
      ])
    }
    return filters
  }

  async load(startTimestamp: number, endTimestamp: number, abortSignal?: AbortSignal) {
    await this.loadData(startTimestamp, endTimestamp, abortSignal)
  }
  abstract loadData(startTimestamp: number, endTimestamp: number, abortSignal?: AbortSignal): Promise<void>

  compileAnnotations(
    renderStart: number,
    renderEnd: number,
    chart: Highcharts.Chart | undefined,
    notifyOpenAnnotationListeners: (component: Component, props: Record<string, unknown>) => void,
    notifyHighlightListeners: (annotationGroupId: string) => void,
    notifyResetHighlightListeners: () => void,
    series: { x: number; y: number | null | undefined }[][] | undefined = undefined,
    extremes: { min: number; max: number; dataMin: number; dataMax: number } | undefined = undefined
  ) {
    const annotations = []
    if (!this.incidents) return
    for (const incident of this.incidents) {
      annotations.push(
        ...this.buildAnnotationsForIncident(
          incident,
          renderStart,
          renderEnd,
          chart,
          notifyOpenAnnotationListeners,
          notifyHighlightListeners,
          notifyResetHighlightListeners,
          series,
          extremes
        )
      )
    }
    this.annotations = annotations
  }

  getAnnotations(): Highcharts.AnnotationsOptions[] {
    return this.annotations
  }
  static fromSerialized(serialized: SerializedChartAnnotatorType, chartEntity: string | undefined): ChartIncidentAnnotator {
    switch (serialized.annotatorType) {
      case ANNOTATOR_TYPES.TIMESPAN: {
        return TimespanChartIncidentAnnotator.fromSerialized(serialized as SerializedTimespanChartIncidentAnnotator, chartEntity)
      }
      case ANNOTATOR_TYPES.BARNDOC: {
        return BarnDocChartAnnotator.fromSerialized(serialized as SerializedBarnDocChartAnnotationProvider, chartEntity)
      }
      case ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY: {
        return SparseDictAnomalyChartAnnotationProvider.fromSerialized(serialized as SerializedSparseDictChartAnnotationProvider, chartEntity)
      }
      default: {
        throw new Error(`Unknown annotator type ${(serialized as SerializedChartAnnotatorType).annotatorType}`)
      }
    }
  }

  serialize(): SerializedChartIncidentAnnotator {
    return {
      annotatorType: this.annotatorType,
      ...(this.additionalFilters?.length && { additionalFilters: this.additionalFilters }),
      ...(this.title && { title: this.title }),
    }
  }

  applySerialization(serialized: BaseSerializedTimespanChartIncidentAnnotator) {
    if (serialized.additionalFilters) this.additionalFilters = serialized.additionalFilters
    if (serialized.title) this.title = serialized.title
  }

  protected abstract buildAnnotationsForIncident(
    incident: Incident,
    renderStart: number,
    renderEnd: number,
    chart: Chart | undefined,
    notifyOpenAnnotationListeners: (component: Component, props: Record<string, unknown>) => void,
    notifyHighlightListeners: (annotationGroupId: string) => void,
    notifyResetHighlightListeners: () => void,
    series: { x: number; y: number | null | undefined }[][] | undefined,
    extremes: { min: number; max: number; dataMin: number; dataMax: number } | undefined
  ): Highcharts.AnnotationsOptions[]

  static findMockPointAndSlopeForXValue(
    x: number,
    data: { x: number; y: number | null | undefined }[],
    windowSizeForSlopeCalculation: number = 5,
    extremes: { min: number; max: number; dataMin: number; dataMax: number }
  ): { slope: number; point: AnnotationMockPointOptionsObject } {
    data = data.sort((a, b) => a.x - b.x)
    const point = data.find(point => point.x === x)
    if (point) {
      const index = data.indexOf(point)
      const startIndex = Math.max(0, index - windowSizeForSlopeCalculation)
      const endIndex = Math.min(data.length - 1, index + windowSizeForSlopeCalculation)
      if (!data[startIndex].y || !data[startIndex].x || !data[endIndex].y || !data[endIndex].x) throw new Error('Invalid Data')
      const slope = (data[endIndex].y - data[startIndex].y) / (data[endIndex].x - data[startIndex].x)
      return { point: { x, y: point.y as number, xAxis: 0, yAxis: 0 }, slope }
    } else {
      const closestPointBefore = data.filter(point => point.x <= x).sort((a, b) => b.x - a.x)[0]
      const closestPointAfter = data.filter(point => point.x >= x).sort((a, b) => a.x - b.x)[0]
      if (closestPointBefore && closestPointAfter) {
        if (!closestPointAfter.y || !closestPointAfter.x || !closestPointBefore.y || !closestPointBefore.x) throw new Error('Invalid Data')
        const slope = (closestPointAfter.y - closestPointBefore.y) / (closestPointAfter.x - closestPointBefore.x)
        const y = slope * (x - closestPointBefore.x) + closestPointBefore.y
        return { point: { x, y, xAxis: 0, yAxis: 0 }, slope }
      } else {
        return { point: { x, y: extremes.min, xAxis: 0, yAxis: 0 }, slope: 0 }
      }
    }
  }

  static findSuitableRenderPositionForLabelAtXValue(
    x: number,
    connected: boolean = true,
    seriesList: { x: number; y: number | null | undefined }[][],
    extremes: { min: number; max: number; dataMin: number; dataMax: number }
  ): { point: AnnotationMockPointOptionsObject; align: AlignValue; verticalAlign: VerticalAlignValue } {
    if (seriesList?.length === 0) return { point: { x: x, y: 0, xAxis: 0, yAxis: 0 }, align: 'center', verticalAlign: 'middle' }

    if (connected) {
      if (seriesList.length === 1) {
        const { point, slope } = ChartIncidentAnnotator.findMockPointAndSlopeForXValue(x, seriesList[0], 5, extremes)
        return {
          point: point,
          verticalAlign: 'middle',
          align: slope > 0 ? 'right' : 'left',
        }
      }

      return {
        point: {
          xAxis: 0,
          yAxis: 0,
          x: x,
          y: extremes.dataMax ?? extremes.max,
        },
        align: 'center',
        verticalAlign: 'top',
      }
    } else {
      const dataMax = extremes.dataMax ?? extremes.max
      const dataMin = extremes.dataMin ?? extremes.min
      if (extremes.max - dataMax > dataMin - extremes.min) {
        return {
          point: {
            xAxis: 0,
            yAxis: 0,
            x: x,
            y: extremes.max - 0.00000001, //there's a bug where highcharts sometimes does not render annotation labels correctly if they sit right at the max/min, so move it just a tiny bit
          },
          align: 'center',
          verticalAlign: 'top',
        }
      }
      return {
        point: {
          xAxis: 0,
          yAxis: 0,
          x: x,
          y: extremes.min + 0.00000001, //there's a bug where highcharts sometimes does not render annotation labels correctly if they sit right at the max/min, so move it just a tiny bit
        },
        align: 'center',
        verticalAlign: 'bottom',
      }
    }
  }

  abstract getTitle(): string
}

abstract class BaseTimespanChartIncidentAnnotator extends ChartIncidentAnnotator {
  private readonly defaultShapeFillColor: string = 'rgba(255,0,0,0.4)'
  abstract readonly incidentTypes: string[]
  shapeFillColor?: string

  async loadData(startTimestamp: number, endTimestamp: number, abortSignal?: AbortSignal): Promise<void> {
    const incidentStore = useIncidentStore()

    const additionalFilters: DocumentQuerySelect[] = []
    if (this.additionalFilters) additionalFilters.push(...this.additionalFilters)
    if (this.transientAdditionalFilters) additionalFilters.push(...this.transientAdditionalFilters)
    this.incidents = await incidentStore.loadIncidentsOpenBetweenSpecificTimestamps(
      this.incidentTypes,
      startTimestamp,
      endTimestamp,
      undefined,
      undefined,
      additionalFilters,
      this.compileWrappedOrFilters(),
      undefined,
      abortSignal
    )
  }

  serialize(): BaseSerializedTimespanChartIncidentAnnotator {
    return {
      ...super.serialize(),
      ...(this.shapeFillColor && { shapeFillColor: this.shapeFillColor }),
    }
  }

  applySerialization(serialized: BaseSerializedTimespanChartIncidentAnnotator) {
    super.applySerialization(serialized)
    if (serialized.shapeFillColor) this.shapeFillColor = serialized.shapeFillColor
  }

  getIncidentTitle(incident: Incident): string {
    return incident.type
  }
  protected buildAnnotationsForIncident(
    incident: Incident,
    renderStart: number,
    renderEnd: number,
    chart: Chart | undefined,
    notifyOpenAnnotationListeners: (component: Component, props: Record<string, unknown>) => void,
    notifyHighlightListeners: (annotationGroupId: string) => void,
    notifyResetHighlightListeners: () => void,
    series: { x: number; y: number | undefined }[][] | undefined,
    extremes: { min: number; max: number; dataMin: number; dataMax: number } | undefined
  ): Highcharts.AnnotationsOptions[] {
    if (!series && chart) {
      series = chart.series.map(s =>
        s.data.map(d => ({
          x: d.x,
          y: d.y,
        }))
      )
    }
    if (!extremes && chart) {
      extremes = chart.yAxis[0].getExtremes()
    }

    if (!series || !extremes) return []
    const annotationGroupId = randomString(20)
    const labelStartTimestamp = Math.max(incident.firstEventTimestamp, renderStart)
    const labelEndTimestamp = Math.min(incident.lastEventTimestamp, renderEnd)
    const shapeStartTimestamp = incident.firstEventTimestamp
    const shapeEndTimestamp = incident.state === 'closed' ? incident.lastEventTimestamp : Date.now() * 1000

    const fillColor = hexToRGBA(incident.displayConfiguration?.displaySettings?.renderColor, 0.4) || this.shapeFillColor || this.defaultShapeFillColor

    const mouseEnterEvent = () => {
      notifyHighlightListeners(annotationGroupId)
    }
    const mouseLeaveEvent = () => {
      notifyResetHighlightListeners()
    }
    const shapeAnnotation: Highcharts.AnnotationsOptions = {
      id: `${annotationGroupId}-shape`,
      events: {
        add: e => {
          if (e && e.target) {
            e.target.graphic.element.onmouseenter = mouseEnterEvent
            e.target.graphic.element.onmouseleave = mouseLeaveEvent

            e.target.graphic.attr({
              'vector-effect': 'non-scaling-stroke',
              'shape-rendering': 'crispEdges',
            })
          }
        },
        remove: () => {
          mouseLeaveEvent()
        },
      },
      zIndex: 3,
      shapes: [
        {
          type: 'path',
          fill: fillColor,
          strokeWidth: 4,
          points: [
            { xAxis: 0, yAxis: 0, x: shapeStartTimestamp, y: extremes.min - 10 * extremes.max }, //bottom left
            { xAxis: 0, yAxis: 0, x: shapeStartTimestamp, y: 10 * extremes.max }, //top left
            { xAxis: 0, yAxis: 0, x: shapeEndTimestamp, y: 10 * extremes.max }, //top right
            { xAxis: 0, yAxis: 0, x: shapeEndTimestamp, y: extremes.min - 10 * extremes.max }, //bottom right
          ],
        },
      ],
      draggable: '',
    }

    const labelRenderPosition = ChartIncidentAnnotator.findSuitableRenderPositionForLabelAtXValue(
      (labelEndTimestamp + labelStartTimestamp) / 2,
      false,
      series,
      extremes
    )

    const labelAnnotation: Highcharts.AnnotationsOptions = {
      id: `${annotationGroupId}-label`,
      events: {
        click: () => {
          notifyOpenAnnotationListeners(IncidentDisplay, { incident: incident })
        },
      },
      draggable: '',
      zIndex: 6,
      crop: false,
      labels: [
        {
          useHTML: false,
          align: labelRenderPosition.align,
          style: { opacity: 1 },
          crop: false,
          shape: 'rect',
          verticalAlign: labelRenderPosition.verticalAlign,
          point: labelRenderPosition.point,
          text: this.getIncidentTitle(incident),
        },
      ],
    }
    return [shapeAnnotation, labelAnnotation]
  }
  getTitle(): string {
    if (this.title) return this.title
    if (this.incidentTypes.length === 1) {
      return t(`incidents.${this.incidentTypes[0]}.title`)
    }
    return t('charts.annotators.timespan')
  }
}

export class TimespanChartIncidentAnnotator extends BaseTimespanChartIncidentAnnotator {
  readonly incidentTypes: string[]
  readonly annotatorType: typeof ANNOTATOR_TYPES.TIMESPAN = ANNOTATOR_TYPES.TIMESPAN
  constructor(chartEntity: string | undefined, incidentTypes: string[]) {
    super(chartEntity)
    this.incidentTypes = incidentTypes
  }

  static fromSerialized(serialized: SerializedTimespanChartIncidentAnnotator, chartEntity: string | undefined): TimespanChartIncidentAnnotator {
    const instance = new TimespanChartIncidentAnnotator(chartEntity, serialized.incidentTypes)
    instance.applySerialization(serialized)
    return instance
  }

  serialize(): SerializedTimespanChartIncidentAnnotator {
    return {
      ...super.serialize(),
      annotatorType: this.annotatorType,
      incidentTypes: this.incidentTypes,
    }
  }
}

export class BarnDocChartAnnotator extends BaseTimespanChartIncidentAnnotator {
  readonly annotatorType: typeof ANNOTATOR_TYPES.BARNDOC = ANNOTATOR_TYPES.BARNDOC
  readonly incidentTypes: string[] = ['barnDocumentation']

  serialize(): SerializedBarnDocChartAnnotationProvider {
    return {
      ...super.serialize(),
      annotatorType: this.annotatorType,
    }
  }

  static fromSerialized(serialized: SerializedBarnDocChartAnnotationProvider, chartEntity: string | undefined): BarnDocChartAnnotator {
    const instance = new BarnDocChartAnnotator(chartEntity)
    instance.applySerialization(serialized)
    return instance
  }

  getIncidentTitle(incident: Incident): string {
    return t('incidents.barnDocumentation.specificationTypes.' + (incident?.messagePayload as BarnDocIncidentMessagePayload).specification.type)
  }
  getTitle(): string {
    if (this.title) return this.title
    return t('charts.annotators.barndoc')
  }
}

export class SparseDictAnomalyChartAnnotationProvider extends BaseTimespanChartIncidentAnnotator {
  readonly annotatorType: typeof ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY = ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY
  readonly incidentTypes: string[] = ['timeSeries']
  private readonly defaultDetectionIdentifier: string = 'sparse_dict_anomaly_detection'
  private detectionIdentifier?: string
  transientAdditionalFilters: DocumentQuerySelect[] = []

  static fromSerialized(
    serialized: SerializedSparseDictChartAnnotationProvider,
    chartEntity: string | undefined
  ): SparseDictAnomalyChartAnnotationProvider {
    const instance = new SparseDictAnomalyChartAnnotationProvider(chartEntity)
    instance.applySerialization(serialized)
    return instance
  }

  applySerialization(serialized: SerializedSparseDictChartAnnotationProvider) {
    super.applySerialization(serialized)
    if (serialized.detectionIdentifier) this.detectionIdentifier = serialized.detectionIdentifier
    this.transientAdditionalFilters = [
      {
        key: 'identifier.detectionIdentifier',
        operator: '==',
        value: this.detectionIdentifier ? this.detectionIdentifier : this.defaultDetectionIdentifier,
      },
    ]
  }

  serialize(): SerializedSparseDictChartAnnotationProvider {
    return {
      ...super.serialize(),
      annotatorType: this.annotatorType,
      ...(this.detectionIdentifier && { detectionIdentifier: this.detectionIdentifier }),
    }
  }
  getTitle(): string {
    if (this.title) return this.title
    return t(`charts.annotators.timeseries.${this.detectionIdentifier ? this.detectionIdentifier : this.defaultDetectionIdentifier}`)
  }
}
